
import React, { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, Upload, X, Check, Image, FileText, FileVideo } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/components/ui/use-toast";

interface MediaLibraryProps {
  onSelect: (url: string) => void;
  onClose: () => void;
  onUpload: (file: File) => Promise<string>;
}

type MediaItem = {
  id: string;
  url: string;
  created_at: string;
  media_type: string;
  caption?: string;
  alt_text?: string;
};

const MediaLibrary = ({ onSelect, onClose, onUpload }: MediaLibraryProps) => {
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      console.log("Fetching media items from database");
      
      // Fixed: Corrected the query syntax for column selection
      const { data, error } = await supabase
        .from('article_media')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching media:', error);
        throw error;
      }
      
      console.log("Media items fetched:", data?.length || 0, "items");
      setMedia(data || []);
    } catch (error: any) {
      console.error('Error fetching media:', error);
      toast({
        variant: "destructive",
        title: "Failed to load media",
        description: error.message || "An unexpected error occurred"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    try {
      setUploading(true);
      console.log("Uploading file:", selectedFile.name);
      
      const url = await onUpload(selectedFile);
      
      if (url) {
        console.log("File uploaded successfully, URL:", url);
        toast({
          title: "Upload successful",
          description: "Your file has been uploaded"
        });
        // Refresh the media list
        await fetchMedia();
        setSelectedFile(null);
      } else {
        throw new Error("Upload failed - no URL returned");
      }
    } catch (error: any) {
      console.error("Error uploading file:", error);
      toast({
        variant: "destructive",
        title: "Upload failed",
        description: error.message || "Failed to upload file"
      });
    } finally {
      setUploading(false);
    }
  };

  const getMediaIcon = (mediaType: string) => {
    switch (mediaType) {
      case 'image':
        return <Image className="h-5 w-5 text-blue-500" />;
      case 'video':
        return <FileVideo className="h-5 w-5 text-red-500" />;
      default:
        return <FileText className="h-5 w-5 text-gray-500" />;
    }
  };

  const filteredMedia = media.filter(item => 
    (item.caption?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    item.alt_text?.toLowerCase().includes(searchTerm.toLowerCase())) ||
    item.url.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="w-full max-w-3xl mx-auto bg-white rounded-lg shadow-lg">
      <div className="p-4 border-b flex justify-between items-center">
        <h2 className="text-xl font-bold">Media Library</h2>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <Tabs defaultValue="browse">
        <TabsList className="w-full grid grid-cols-2 mb-4">
          <TabsTrigger value="browse">Browse Media</TabsTrigger>
          <TabsTrigger value="upload">Upload New</TabsTrigger>
        </TabsList>

        <TabsContent value="browse" className="p-4">
          <div className="mb-4 relative">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search media..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <ScrollArea className="h-[400px]">
            {loading ? (
              <div className="flex justify-center items-center h-40">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredMedia.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {searchTerm ? "No media found matching your search" : "No media found. Upload some!"}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-4">
                {filteredMedia.map((item) => (
                  <div 
                    key={item.id} 
                    className="relative group cursor-pointer border rounded-md overflow-hidden hover:ring-2 hover:ring-primary"
                    onClick={() => {
                      console.log("Media item selected:", item);
                      onSelect(item.url);
                    }}
                  >
                    {item.media_type === 'image' ? (
                      <img 
                        src={item.url} 
                        alt={item.alt_text || "Media"} 
                        className="w-full aspect-square object-cover"
                        onError={(e) => {
                          console.error("Image failed to load:", item.url);
                          (e.target as HTMLImageElement).src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect width='18' height='18' x='3' y='3' rx='2' ry='2'/%3E%3Ccircle cx='9' cy='9' r='2'/%3E%3Cpath d='m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21'/%3E%3C/svg%3E";
                          (e.target as HTMLImageElement).className = "w-full aspect-square object-contain p-8 bg-gray-100";
                        }}
                      />
                    ) : (
                      <div className="w-full aspect-square flex items-center justify-center bg-gray-100">
                        {getMediaIcon(item.media_type)}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                      <Check className="h-8 w-8 text-white" />
                    </div>
                    {item.caption && (
                      <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white p-1 text-xs truncate">
                        {item.caption}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        <TabsContent value="upload" className="p-4">
          <div className="border-2 border-dashed rounded-md p-6 text-center">
            <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="mb-2">Drag and drop a file or click to browse</p>
            <Input
              type="file"
              accept="image/*,video/*"
              onChange={handleFileChange}
              className="mb-4"
            />
            {selectedFile && (
              <div className="mt-2 text-sm text-muted-foreground">
                Selected: {selectedFile.name}
              </div>
            )}
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className="mt-4"
            >
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Upload
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MediaLibrary;
